import React from 'react'
import { BlockCustomPartnerLogos } from '@blocks/BlockCustomPartnerLogos'
import { BlockDealConditions, BlockDealConditionsData } from '@blocks/BlockDealConditions'
import { BlockDisclaimerCentralBank } from '@blocks/BlockDisclaimerCentralBank'
import { BlockFeedback, BlockFeedbackData } from '@blocks/BlockFeedback'
import { BlockMobileApp } from '@blocks/BlockMobileApp'
import { BlockTextCols, BlockTextColsData } from '@blocks/BlockTextCols'
import { ApplicationForms } from '@shared/components/forms/application'
import { scrollToApplicationForm } from '@shared/components/forms/application/cross'
import { ApplicationFormData } from '@shared/components/forms/application/cross-new/types'
import { AboutCredit, CustomCreditRate } from '@shared/components/sections/about-credit'
import { AppSection } from '@shared/components/sections/app'
import { BlocksWithIcons } from '@shared/components/sections/blocks-with-icons'
import { CreditAlternative } from '@shared/components/sections/credit-alternative'
import { CreditConditions } from '@shared/components/sections/credit-conditions'
import { CreditRepaymentMethods } from '@shared/components/sections/credit-repayment-methods'
import { FormSection } from '@shared/components/sections/custom-partner-form-section'
import { DisclaimerMain } from '@shared/components/sections/disclamer-main'
import { Feedback } from '@shared/components/sections/feedback'
import { Journal } from '@shared/components/sections/journal'
import { LoanBonus } from '@shared/components/sections/loan-bonus'
import { OurManagers } from '@shared/components/sections/our-managers'
import { Process } from '@shared/components/sections/process'
import { StepsWithTitle } from '@shared/components/sections/steps-with-title'
import { YouTube } from '@shared/components/sections/youtube'

import { useNodeParams, useVarsReplacer } from '../../hooks/useNodeParams'
import { AvailableComponentTypes, AvailablePageNodes, PageNodeViewModel } from '../../types'
import { safeParseJsonValue } from '../../utils'
import { NodeFaq } from '../Faq'
import { NodeHtmlText } from '../HtmlText'
import { NodeMain } from '../NodeMain'
import { NodeTabs } from '../Tabs'
import dynamic from 'next/dynamic'

export const AdminPageNode = ({
	nodeViewModel,
}: {
	nodeViewModel: PageNodeViewModel | null
}): JSX.Element | null => {
	const node = nodeViewModel?.value || ({} as any)
	const nodeParams = useNodeParams(node.nodeParam)
	const varsReplacer = useVarsReplacer()

	switch (node.name) {
		case AvailablePageNodes.main_block:
			return nodeViewModel ? <NodeMain node={nodeViewModel} /> : null
		case AvailablePageNodes.tabs:
			return nodeViewModel ? <NodeTabs node={nodeViewModel} Node={AdminPageNode} /> : null
		case AvailablePageNodes.application_form_cross:
			return <ApplicationForms.Cross formTitle={nodeParams.get(AvailableComponentTypes.title)} />
		case AvailablePageNodes.credit_conditions: {
			const json: any = safeParseJsonValue(nodeParams.get(AvailableComponentTypes.json), {})
			return (
				<CreditConditions
					isVisibleConditions={json.isConditionVisible}
					loanDocuments={json.documents}
					loanParticipantRequirements={json.borrower}
					title={nodeParams.get(AvailableComponentTypes.blockTitle)}
				/>
			)
		}
		case AvailablePageNodes.credit_repayment_methods:
			return (
				<CreditRepaymentMethods
					data={safeParseJsonValue(nodeParams.get(AvailableComponentTypes.json), []).map(
						(item: any) => {
							const Icon = dynamic(
								() =>
									import(
										`@shared/svg/in-shape/${item.iconSvgName.replace('InShape', '').toLowerCase()}.svg`
									)
							)
							return {
								...item,
								IconSvg: Icon,
							}
						}
					)}
				/>
			)
		case AvailablePageNodes.credit_alternative:
			return (
				<CreditAlternative
					title={nodeParams.get(AvailableComponentTypes.title)}
					subtitle={nodeParams.get(AvailableComponentTypes.description)}
					customData={safeParseJsonValue(nodeParams.get(AvailableComponentTypes.json), [])}
				/>
			)
		case AvailablePageNodes.credit_about: {
			const json: any = safeParseJsonValue(nodeParams.get(AvailableComponentTypes.json), {})
			const bannerItems = nodeParams
				.get(AvailableComponentTypes.stringArray, undefined)
				?.split('\n')
			const percentVarKey = '%loan_percent%'
			const percent = varsReplacer(percentVarKey)

			return (
				<AboutCredit
					mainTitle={nodeParams.get(AvailableComponentTypes.blockTitle)}
					hint={nodeParams.get(AvailableComponentTypes.description)}
					title={nodeParams.get(AvailableComponentTypes.title)}
					customBannerItems={bannerItems}
					customPercent={percent !== percentVarKey ? percent : undefined}
					rateMode={json.bannerMode === 'car' ? 'car' : 'pledge'}
					points={(json?.data || []).map((item: any) => {
						const Icon = dynamic(
							() =>
								import(
									`@shared/svg/in-shape/${item.iconSvgName.replace('InShape', '').toLowerCase()}.svg`
								)
						)
						return {
							...item,
							IconSvg: Icon,
						}
					})}
				/>
			)
		}
		case AvailablePageNodes.how_to_loan:
			return (
				<Process
					title={nodeParams.get(AvailableComponentTypes.title)}
					subtitle={nodeParams.get(AvailableComponentTypes.description)}
					onButtonClick={scrollToApplicationForm}
					points={safeParseJsonValue(nodeParams.get(AvailableComponentTypes.json), [])}
				/>
			)
		case AvailablePageNodes.feedback:
			return <Feedback title={nodeParams.get(AvailableComponentTypes.title)} />
		case AvailablePageNodes.feedback_custom:
			return (
				<BlockFeedback
					blockTitle={nodeParams.get(AvailableComponentTypes.blockTitle)}
					data={safeParseJsonValue<BlockFeedbackData>(
						nodeParams.get(AvailableComponentTypes.json),
						[]
					)}
				/>
			)
		case AvailablePageNodes.our_specialists:
			return <OurManagers />
		case AvailablePageNodes.faq:
			return nodeViewModel ? <NodeFaq node={nodeViewModel} Node={AdminPageNode} /> : null
		case AvailablePageNodes.html_text:
			return <NodeHtmlText stringValue={nodeParams.get(AvailableComponentTypes.htmlText, '')} />
		case AvailablePageNodes.told_about_pledge:
			return (
				<>
					<Journal />
					<YouTube />
				</>
			)
		case AvailablePageNodes.mobile_application:
			return <AppSection />
		case AvailablePageNodes.steps: {
			const steps: string[] = nodeParams.get(AvailableComponentTypes.stringArray, '').split('\n')
			return <StepsWithTitle title={nodeParams.get(AvailableComponentTypes.title)} steps={steps} />
		}
		case AvailablePageNodes.blocks_with_icons:
			return <BlocksWithIcons title={nodeParams.get(AvailableComponentTypes.title)} />
		case AvailablePageNodes.credit_order:
			return (
				<ApplicationForms.CrossNew
					processBarText='Вероятность одобрения кредита'
					formTitle={nodeParams.get(AvailableComponentTypes.title, 'Заявка на кредит')}
					loanPercent={9.5}
					data={safeParseJsonValue<ApplicationFormData>(
						nodeParams.get(AvailableComponentTypes.json),
						{}
					)}
				/>
			)
		case AvailablePageNodes.bonus_calculator:
			return <LoanBonus />
		case AvailablePageNodes.disclamer_main:
			return <DisclaimerMain />
		case AvailablePageNodes.credit_rate:
			return (
				<CustomCreditRate
					items={nodeParams.get(AvailableComponentTypes.stringArray, '').split('\n')}
					title={nodeParams.get(AvailableComponentTypes.title)}
					subtitle={nodeParams.get(AvailableComponentTypes.description)}
				/>
			)
		case AvailablePageNodes.deal_conditions:
			return (
				<BlockDealConditions
					blockTitle={nodeParams.get(AvailableComponentTypes.blockTitle)}
					data={safeParseJsonValue<BlockDealConditionsData>(
						nodeParams.get(AvailableComponentTypes.json),
						{}
					)}
				/>
			)
		case AvailablePageNodes.partner_form_section: {
			const summary: { summary: { icon: string; text: string }[] } = JSON.parse(
				nodeParams.get<string>(AvailableComponentTypes.json)
			)
			return <FormSection title={nodeParams.get(AvailableComponentTypes.title)} data={summary} />
		}
		case AvailablePageNodes.partner_logos: {
			return (
				<BlockCustomPartnerLogos
					img={nodeParams.get(AvailableComponentTypes.url)}
					link={nodeParams.get(AvailableComponentTypes.description)}
				/>
			)
		}
		case AvailablePageNodes.text_cols:
			return (
				<BlockTextCols
					blockTitle={nodeParams.get(AvailableComponentTypes.blockTitle)}
					buttonTitle={nodeParams.get(AvailableComponentTypes.title)}
					description={nodeParams.get(AvailableComponentTypes.description)}
					data={safeParseJsonValue<BlockTextColsData>(
						nodeParams.get(AvailableComponentTypes.json),
						[]
					)}
					onButtonClick={scrollToApplicationForm}
				/>
			)

		case AvailablePageNodes.app_custom: {
			const items: Array<string> = nodeParams
				.get(AvailableComponentTypes.stringArray, '')
				.split('\n')
			return <BlockMobileApp title={nodeParams.get(AvailableComponentTypes.title)} items={items} />
		}

		case AvailablePageNodes.disclaimer_central_bank: {
			return <BlockDisclaimerCentralBank />
		}
		default:
			return null
	}
}
